import { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { createSelector } from "reselect"
import { ConsultingRevenueVsBudgetSliceState } from "slices/finance/consulting-revenue-budget/reducer"
import { getConsultingRevenueVsBudget, downloadConsultingRevenueVsBudgetCSV } from "slices/thunk"
import { setQuery } from "slices/finance/consulting-revenue-budget/reducer"
import { IConsultingRevenueVsBudgetQuery, RevenueVsBudgetRow } from "models/consulting-revenue-budget"
import GraphTemplate from "Components/templates/graph-template"
import RevenueBudgetChart from "Components/molecule/charts/revenue-budget-chart"
import SearchConsultingRevenueVsBudgetForm from "Components/organisms/forms/search-consulting-revenue-vs-budget-form"
import ActionButton from "Components/atoms/buttons/action-button"
import { HelpIcon } from "Components/atoms/icons"
import ReportButtons from "Components/molecule/report-buttons"
import useManual from "hooks/useManual"
import CheckboxInput from "Components/molecule/inputs/checkbox-input"
import FiscalCalendar, { DEFAULT_FISCAL_START } from "utils/fiscal-calendar"

const selectState = createSelector(
    (state: any) => state.ConsultingRevenueVsBudget,
    ({ query, byDivision, byBranch, byDepartment }: ConsultingRevenueVsBudgetSliceState) => ({ query, byDivision, byBranch, byDepartment })
)

const ConsultingRevenueVsBudgetPage = () => {
    document.title = "Consulting Revenue vs Budget | IbexIQ"
    const { handleOpenManual } = useManual("finance/consulting-revenue-budget");
    const dispatch = useDispatch() as any
    const { query, byDivision, byBranch, byDepartment } = useSelector(selectState)
    const [isLoading, setIsLoading] = useState(true)
    const [isExporting, setIsExporting] = useState(false)
    const [activeOnly, setActiveOnly] = useState(true)

    const filter = (rows: typeof byDivision) => activeOnly
        ? rows.filter(r => r.id === 'TOTAL' || r.actual !== 0 || r.budget !== 0 || r.variance !== 0)
        : rows

    const departmentGroups = useMemo(() => {
        const byParent = new Map<string, RevenueVsBudgetRow[]>()
        byDepartment.forEach(row => {
            if (!row.parentId) return
            const list = byParent.get(row.parentId) ?? []
            list.push(row)
            byParent.set(row.parentId, list)
        })

        return byDivision
            .map(division => ({
                division,
                rows: filter(byParent.get(division.id) ?? [])
            }))
            .filter(({ rows }) => !activeOnly || rows.length > 0)
    }, [byDepartment, byDivision, activeOnly])

    const fiscalPeriodOptions = useMemo(() => {
        const fiscal = new FiscalCalendar(DEFAULT_FISCAL_START)
        const options: { value: string; label: string }[] = []
        const today = new Date()
        const { fiscalYear: currentFY, period: currentPeriod } = fiscal.dateToFiscal(today)

        for (let fy = 2019; fy <= currentFY + 1; fy++) {
            const maxPeriod = fy === currentFY + 1 ? currentPeriod : 12
            for (let p = 1; p <= maxPeriod; p++) {
                options.push({
                    value: fy.toString() + String(p).padStart(2, '0'),
                    label: fiscal.fiscalToLabel(fy, p),
                })
            }
        }

        return options
    }, [])

    const handleChangeQuery = (key: string, value: any) => {
        dispatch(setQuery({ ...query, [key]: value }))
    }

    const handleSearch = async () => {
        setIsLoading(true)
        await dispatch(getConsultingRevenueVsBudget(query))
        setIsLoading(false)
    }

    const handleClearSearch = async () => {
        const fiscal = new FiscalCalendar(DEFAULT_FISCAL_START)
        const lastMonth = FiscalCalendar.getLastMonthPeriod()
        const defaultQuery: IConsultingRevenueVsBudgetQuery = { fromPeriod: lastMonth, toPeriod: lastMonth }
        dispatch(setQuery(defaultQuery))
        setIsLoading(true)
        await dispatch(getConsultingRevenueVsBudget(defaultQuery))
        setIsLoading(false)
    }

    const handleExportData = () => {
        setIsExporting(true)
        try {
            downloadConsultingRevenueVsBudgetCSV(byDivision, byBranch, byDepartment, query)
        } finally {
            setIsExporting(false)
        }
    }

    useEffect(() => {
        dispatch(getConsultingRevenueVsBudget(query))
            .then(() => setIsLoading(false))
    }, [])

    return (
        <GraphTemplate
            title="Finance"
            breadcrumbItem="Consulting Revenue vs Budget"
            isLoading={isLoading}
            actions={<ActionButton onClick={handleOpenManual}>Help <HelpIcon /></ActionButton>}
            tableActions={
                <>
                    <CheckboxInput
                        label="Active Only"
                        checked={activeOnly}
                        onChange={() => setActiveOnly(prev => !prev)}
                    />
                    <ReportButtons title="Consulting Revenue vs Budget" module="Finance" fullExport={handleExportData} />
                </>
            }
            filters={
                <SearchConsultingRevenueVsBudgetForm
                    query={query}
                    fiscalPeriodOptions={fiscalPeriodOptions}
                    isSearching={isLoading}
                    handleChangeQuery={handleChangeQuery}
                    handleSearch={handleSearch}
                    handleClearSearch={handleClearSearch}
                />
            }
        >
            <div className="section-container">
                <RevenueBudgetChart title="By Branch" data={filter(byBranch)} />
            </div>
            <div className="section-container">
                <RevenueBudgetChart title="By Division" data={filter(byDivision)} />
            </div>
            {departmentGroups.map(({ division, rows }) => (
                <div key={division.id} className="section-container">
                    <RevenueBudgetChart title={`By Department — ${division.name}`} data={rows} />
                </div>
            ))}
        </GraphTemplate>
    )
}

export default ConsultingRevenueVsBudgetPage
